import type { Address } from '@/lib/services/addresses/addresses.types'
import type {
  SalesOrder,
  UpdateSalesOrderInput,
} from '@/lib/services/sales-orders/sales-orders.types'
import type { AdminAddressVerificationState } from '@/components/orders/admin-address-verification-labels'
import { Country } from '@/lib/enums/Country'
import { AddressVerificationState } from '@/lib/enums/AddressVerificationState'
import { withAdminAddressVerificationLabel } from '@/components/orders/admin-address-verification-labels'
import { normalizeAddressRegionCode } from '@/lib/address-region-options'
import { buildLimitedOrderDeliveryAddressFields } from '@/lib/order-address-limits'

/** Saved address fields plus the numeric coordinates an in-progress delivery draft can hold. */
type DeliveryAddressDraft = Partial<
  Omit<Address, 'id' | 'created_at' | 'updated_at' | 'lat' | 'lng' | 'is_verified'>
> &
  Pick<NonNullable<UpdateSalesOrderInput['delivery_address']>, 'lat' | 'lng' | 'is_verified'>
/** The existing address builder owns place fields; these two raw geocoder flags are only compared. */
type SuggestedDeliveryAddress = NonNullable<
  Parameters<typeof buildLimitedOrderDeliveryAddressFields>[0]
> & { locationType?: unknown; partialMatch?: unknown }
type ComparableDeliveryAddress = Record<
  'line1' | 'city' | 'state' | 'postal_code' | 'country',
  string
>
type ProjectedDeliveryDestination = Partial<
  Pick<SalesOrder, 'delivery_destination_name' | 'recipient_name'>
> & { delivery_address?: DeliveryAddressDraft | null }
type AddressMetaKeys =
  | 'postal_code_suffix'
  | 'google_place_id'
  | 'lat'
  | 'lng'
  | 'is_verified'
  | 'is_rooftop'
  | 'verification_state'
type ResetVerification = {
  postal_code_suffix: null
  google_place_id: null
  lat: null
  lng: null
  is_verified: false
  is_rooftop: false
  verification_state: typeof AddressVerificationState.Unverified.Value
}
type ManualVerification = Omit<ResetVerification, 'is_verified' | 'verification_state'> & {
  is_verified: true
  verification_state: typeof AddressVerificationState.ManuallyVerified.Value
}
type SuggestedAddressFields = ReturnType<typeof buildLimitedOrderDeliveryAddressFields> & {
  country: DeliveryAddressDraft['country']
  google_place_id: Address['google_place_id']
  lat: number | null
  lng: number | null
  is_verified: true
  is_rooftop: boolean
  verification_state:
    | typeof AddressVerificationState.Verified.Value
    | typeof AddressVerificationState.VerifiedApproximate.Value
}
type EmptyDeliveryAddress = Record<
  'name' | 'line1' | 'line2' | 'city' | 'state' | 'postal_code' | 'country',
  string
> &
  Required<Pick<DeliveryAddressDraft, AddressMetaKeys>>

export function createComparableDeliveryAddress(
  address: DeliveryAddressDraft | null | undefined
): ComparableDeliveryAddress {
  return {
    line1: typeof address?.line1 === 'string' ? address.line1.trim() : '',
    city: typeof address?.city === 'string' ? address.city.trim() : '',
    state: normalizeAddressRegionCode(address?.state),
    postal_code: typeof address?.postal_code === 'string' ? address.postal_code.trim() : '',
    country: typeof address?.country === 'string' ? address.country.trim() : '',
  }
}

export function resolveDeliveryDestinationName(
  address: Pick<DeliveryAddressDraft, 'name'> | null | undefined,
  recipientName: SalesOrder['recipient_name'] | undefined
): string {
  const name = String(address?.name ?? '').trim()
  const recipient = String(recipientName ?? '').trim()
  const normalizedName = name.replace(/\s+/g, ' ').toLocaleLowerCase()
  const normalizedRecipient = recipient.replace(/\s+/g, ' ').toLocaleLowerCase()

  return name && normalizedName !== normalizedRecipient ? name : ''
}

export function resolveProjectedDeliveryDestinationName(
  record: ProjectedDeliveryDestination | null | undefined
): string {
  if (record && Object.hasOwn(record, 'delivery_destination_name')) {
    return String(record.delivery_destination_name ?? '').trim()
  }

  return resolveDeliveryDestinationName(record?.delivery_address, record?.recipient_name)
}

function normalizeCoreAddressText(value: unknown): string {
  // oxlint-disable-next-line typescript/no-base-to-string -- preserve existing coercion of raw address comparison values
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
}

function normalizeCoreAddress(
  address: DeliveryAddressDraft = {}
): Pick<ComparableDeliveryAddress, 'line1' | 'city' | 'state' | 'postal_code'> {
  return {
    line1: normalizeCoreAddressText(address.line1),
    city: normalizeCoreAddressText(address.city),
    state: normalizeAddressRegionCode(address.state).toUpperCase(),
    postal_code: String(address.postal_code ?? '').trim(),
  }
}

export function createDeliveryAddressMeta(
  address: DeliveryAddressDraft | null | undefined
): Required<Pick<DeliveryAddressDraft, AddressMetaKeys>> {
  return {
    postal_code_suffix: address?.postal_code_suffix ?? null,
    google_place_id: address?.google_place_id ?? null,
    lat: address?.lat ?? null,
    lng: address?.lng ?? null,
    is_verified: address?.is_verified ?? false,
    is_rooftop: address?.is_rooftop ?? false,
    verification_state: address?.verification_state ?? null,
  }
}

export function clearDeliveryAddressVerification<TAddress extends DeliveryAddressDraft>(
  address: TAddress
): Omit<TAddress, keyof ResetVerification> & ResetVerification
export function clearDeliveryAddressVerification(address?: undefined): ResetVerification
export function clearDeliveryAddressVerification(
  address: DeliveryAddressDraft = {}
): DeliveryAddressDraft & ResetVerification {
  return {
    ...address,
    postal_code_suffix: null,
    google_place_id: null,
    lat: null,
    lng: null,
    is_verified: false,
    is_rooftop: false,
    verification_state: AddressVerificationState.Unverified.Value,
  }
}

export function createManuallyVerifiedAddress<TAddress extends DeliveryAddressDraft>(
  address: TAddress
): Omit<TAddress, keyof ManualVerification> & ManualVerification
export function createManuallyVerifiedAddress(address?: undefined): ManualVerification
export function createManuallyVerifiedAddress(
  address: DeliveryAddressDraft = {}
): DeliveryAddressDraft & ManualVerification {
  return {
    ...address,
    postal_code_suffix: null,
    google_place_id: null,
    lat: null,
    lng: null,
    is_verified: true,
    is_rooftop: false,
    verification_state: AddressVerificationState.ManuallyVerified.Value,
  }
}

export function applySuggestedDeliveryAddress<TAddress extends DeliveryAddressDraft>(
  address: TAddress,
  suggested?: SuggestedDeliveryAddress
): Omit<TAddress, keyof SuggestedAddressFields> &
  Omit<SuggestedAddressFields, 'country'> & { country: string | TAddress['country'] }
export function applySuggestedDeliveryAddress(
  address?: undefined,
  suggested?: SuggestedDeliveryAddress
): SuggestedAddressFields
export function applySuggestedDeliveryAddress(
  address: DeliveryAddressDraft = {},
  suggested: SuggestedDeliveryAddress = {}
): DeliveryAddressDraft & SuggestedAddressFields {
  const limitedAddressFields = buildLimitedOrderDeliveryAddressFields(suggested, address)

  return {
    ...address,
    ...limitedAddressFields,
    country: suggested.country || address.country,
    google_place_id: suggested.placeId || null,
    lat: suggested.lat ?? null,
    lng: suggested.lng ?? null,
    is_verified: true,
    is_rooftop: suggested.locationType === 'ROOFTOP' && suggested.partialMatch !== true,
    verification_state:
      suggested.locationType === 'ROOFTOP' && suggested.partialMatch !== true
        ? AddressVerificationState.Verified.Value
        : AddressVerificationState.VerifiedApproximate.Value,
  }
}

export function createEmptyDeliveryAddress(
  address: DeliveryAddressDraft | null = {}
): EmptyDeliveryAddress {
  return {
    name: address?.name || '',
    line1: address?.line1 || '',
    line2: address?.line2 || '',
    city: address?.city || '',
    state: normalizeAddressRegionCode(address?.state),
    postal_code: address?.postal_code || '',
    country: address?.country || Country.UnitedStates.Value,
    lat: address?.lat ?? null,
    lng: address?.lng ?? null,
    postal_code_suffix: address?.postal_code_suffix ?? null,
    google_place_id: address?.google_place_id ?? null,
    is_verified: address?.is_verified ?? false,
    is_rooftop: address?.is_rooftop ?? false,
    verification_state: address?.verification_state ?? null,
  }
}

export function isSuggestedAddressUsable(
  suggestedAddress: SuggestedDeliveryAddress | null | undefined
): boolean {
  if (!suggestedAddress) return false

  const suggestedHasCoords = suggestedAddress.lat != null && suggestedAddress.lng != null
  const suggestedHasPlaceId = Boolean(suggestedAddress.placeId)
  const suggestedHasStreetNumber = Boolean(suggestedAddress.street_number)
  const suggestedHasRoute = Boolean(suggestedAddress.route)
  const suggestedHasPostal = Boolean(suggestedAddress.postal_code)

  return (
    suggestedHasCoords &&
    suggestedHasPlaceId &&
    suggestedHasStreetNumber &&
    suggestedHasRoute &&
    suggestedHasPostal &&
    suggestedAddress.partialMatch !== true
  )
}

export function suggestedAddressMatchesTypedCore(
  typedAddress: DeliveryAddressDraft = {},
  suggestedAddress: SuggestedDeliveryAddress = {}
): boolean {
  if (!isSuggestedAddressUsable(suggestedAddress)) {
    return false
  }

  const typed = normalizeCoreAddress(typedAddress)
  const suggested = normalizeCoreAddress(
    buildLimitedOrderDeliveryAddressFields(suggestedAddress, typedAddress)
  )

  return (
    ['line1', 'city', 'state', 'postal_code'] satisfies (keyof ReturnType<
      typeof normalizeCoreAddress
    >)[]
  ).every((field) => typed[field] !== '' && typed[field] === suggested[field])
}

export function buildDeliveryAddressSearchString(address: DeliveryAddressDraft = {}): string {
  return [
    address.line1,
    address.line2,
    `${address.city || ''}${address.state ? `, ${address.state}` : ''} ${address.postal_code || ''}`.trim(),
    address.country || Country.UnitedStates.Value,
  ]
    .filter(Boolean)
    .join(', ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function getDraftDeliveryAddressVerificationState(
  address: DeliveryAddressDraft | null | undefined
): AdminAddressVerificationState | null {
  const backendState = address?.verification_state
  if (backendState === AddressVerificationState.Verified.Value) {
    return withAdminAddressVerificationLabel(AddressVerificationState.Verified)
  }

  if (backendState === AddressVerificationState.VerifiedApproximate.Value) {
    return withAdminAddressVerificationLabel(AddressVerificationState.VerifiedApproximate)
  }

  if (backendState === AddressVerificationState.ManuallyVerified.Value) {
    return withAdminAddressVerificationLabel(AddressVerificationState.ManuallyVerified)
  }

  if (backendState === AddressVerificationState.Unverified.Value) {
    return withAdminAddressVerificationLabel(AddressVerificationState.Unverified)
  }

  if (address?.is_verified === true) {
    return withAdminAddressVerificationLabel(
      address?.google_place_id || (address?.lat != null && address?.lng != null)
        ? address?.is_rooftop
          ? AddressVerificationState.Verified
          : AddressVerificationState.VerifiedApproximate
        : AddressVerificationState.ManuallyVerified
    )
  }

  if (address?.line1 || address?.city || address?.state || address?.postal_code) {
    return withAdminAddressVerificationLabel(AddressVerificationState.Unverified)
  }

  return null
}
